import * as React from 'react'

import { useLocalStorage } from './useStorage'

export enum Flags {
  FEATURE_FLAGS_ENABLED = 'FEATURE_FLAGS_ENABLED',
  ONBOARDING = 'ONBOARDING',
  SSO = 'SSO',
  SSO_PROFILE = 'SSO_PROFILE',
  SSO_PARENT_PROGRESSIVE_SIGNUP = 'SSO_PARENT_PROGRESSIVE_SIGNUP',
  ALLOW_PARENT_TO_SIGN_UP_ON_WHITE_LABELED_SITE = 'ALLOW_PARENT_TO_SIGN_UP_ON_WHITE_LABELED_SITE',
}

export type FlagsKeys = keyof typeof Flags
export type FlagsValues = `${Flags}`

const reverseMap = new Map<string, Flags>()
Object.values(Flags).forEach((value: Flags) => {
  reverseMap.set(value.toString(), value)
})
export function FlagsValueOf(str: string) {
  return reverseMap.get(str)
}

type CurrentFlags = {
  [key in Flags]: boolean | undefined
}

export const currentFlags: CurrentFlags = {
  [Flags.FEATURE_FLAGS_ENABLED]: process.env.NODE_ENV === 'development',
  //
  [Flags.ONBOARDING]: undefined,
  [Flags.SSO]: true,
  [Flags.SSO_PROFILE]: false,
  [Flags.SSO_PARENT_PROGRESSIVE_SIGNUP]: false,
  [Flags.ALLOW_PARENT_TO_SIGN_UP_ON_WHITE_LABELED_SITE]: undefined,
}

export interface IFeatureFlag {
  name: () => string
  enabled: () => boolean
  set: (value: boolean) => void
  toggle: () => void
}

export function useFeatureFlag(myFlag: Flags): IFeatureFlag {
  const internalName: string = `ff-${myFlag}`

  const [flag, setFlag, removeFlag] = useLocalStorage(internalName, currentFlags[myFlag])

  React.useEffect(() => {
    if (flag === undefined || flag === null) {
      removeFlag(flag)
    }
  }, [flag])

  function name(): string {
    return myFlag
  }

  function enabled(): boolean {
    return flag || false
  }

  function set(value: boolean): void {
    setFlag(value)
  }

  function toggle(): void {
    setFlag(!enabled())
  }

  return { enabled, toggle, name, set }
}

export function useAllFlags(): IFeatureFlag[] {
  const flagKeys = Object.keys(currentFlags).filter(flag => flag !== Flags.FEATURE_FLAGS_ENABLED)

  const flagList: Flags[] = flagKeys
    .map(key => FlagsValueOf(key as FlagsValues))
    .filter((flag?: Flags) => !!flag) as Flags[]
  const allFlags = flagList.map((flag: Flags) => {
    return useFeatureFlag(flag) as IFeatureFlag
  })
  return allFlags
}
